import { Group, Text, UnstyledButton } from '@mantine/core';
import { useProfile } from 'init/use-enforce-profile';
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  INTEGRATION_ROUTE,
  INTEGRATIONS_ROUTE,
} from 'shared/components/Integrations/Integrations.constants';
import type { Sync, SyncState } from 'shared/hooks/use-load-syncs';

import { useSyncStatusLabel } from '../hooks/use-sync-status-label';
import { useSyncTitle } from '../hooks/use-sync-title';
import { SyncDot } from './SyncDot';

export const STATE_BACKGROUNDS: Record<SyncState, string> = {
  never_synced: 'gray.1',
  running: 'purple.1',
  healthy: 'gray.0',
  failing: 'error.0',
};

export const STATE_BORDERS: Record<SyncState, string> = {
  never_synced: 'gray.3',
  running: 'purple.3',
  healthy: 'gray.3',
  failing: 'error.3',
};

export const STATE_TEXT_COLORS: Record<SyncState, string> = {
  never_synced: 'gray.7',
  running: 'purple.7',
  healthy: 'gray.6',
  failing: 'error.8',
};

const SYNC_SETTINGS_ROUTE = {
  ...INTEGRATION_ROUTE,
  email: '/settings/configuration',
};

const integrationSettingsRoute = (integrationKey: string) =>
  Object.entries(SYNC_SETTINGS_ROUTE).find(
    ([integration]) => integration === integrationKey,
  )?.[1] ?? INTEGRATIONS_ROUTE;

type SyncSettingsLinkProps = {
  integrationKey: string;
  children: JSX.Element;
};

export const SyncSettingsLink = ({ integrationKey, children }: SyncSettingsLinkProps) => {
  const profile = useProfile();

  if (!profile.isAdmin) return children;

  return (
    <UnstyledButton component={Link} to={integrationSettingsRoute(integrationKey)}>
      {children}
    </UnstyledButton>
  );
};

type SyncPillProps = {
  sync: Sync;
};

export const SyncPill = ({ sync }: SyncPillProps) => {
  const syncStatusLabel = useSyncStatusLabel();
  const syncTitle = useSyncTitle();

  return (
    <SyncSettingsLink integrationKey={sync.integrationKey}>
      <Group
        bg={STATE_BACKGROUNDS[sync.state]}
        bd={`1px solid ${STATE_BORDERS[sync.state]}`}
        justify="space-between"
        wrap="nowrap"
        gap="space2"
        px="space3"
        py="space2"
        style={{ borderRadius: 8 }}
      >
        <Group gap="space2" wrap="nowrap" style={{ minWidth: 0 }}>
          <SyncDot state={sync.state} />
          <Text fw={600} fz="sm" c={STATE_TEXT_COLORS[sync.state]} truncate>
            {syncTitle(sync)}
          </Text>
        </Group>
        <Text
          fz="xs"
          c={STATE_TEXT_COLORS[sync.state]}
          style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {syncStatusLabel(sync)}
        </Text>
      </Group>
    </SyncSettingsLink>
  );
};
